// "Booking Requests" card for the portfolio-analytics Overview tab. Kept out
// of app-portfolio-analytics.tsx (near the pages/** size cap). Renders
// nothing when the account lacks the booking_requests entitlement (the stats
// endpoint 403s) or the fetch fails — the Overview tab must not break for
// accounts without the feature.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronRight } from "lucide-react";
import { computeDateRange } from "@/components/DateRangeFilter";
import { BookingStatusPie } from "@/components/booking-stats-charts";
import type { BookingStatsResponse } from "@shared/booking-stats";

export function BookingRequestsOverviewCard() {
  // Overview has no date control; a rolling 90 days is the card's fixed lens.
  const range = useMemo(() => computeDateRange("last_90_days"), []);

  const statsQuery = useQuery<BookingStatsResponse>({
    queryKey: ["/api/app/booking-requests/stats", range.from, range.to, "overview"],
    queryFn: async () => {
      const res = await fetch(`/api/app/booking-requests/stats?from=${range.from}&to=${range.to}`, { credentials: "include" });
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Entitlement-gated or failed → the card simply doesn't exist.
  if (statsQuery.isLoading || statsQuery.isError || !statsQuery.data) return null;
  const { totals, byStatus } = statsQuery.data;

  return (
    <Card data-testid="card-booking-requests-overview">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Booking Requests — last 90 days</CardTitle>
          <Link
            href="/app/members/booking-requests?tab=stats"
            className="text-xs text-brand hover:underline flex items-center gap-0.5"
            data-testid="link-open-booking-stats"
          >
            Open booking stats <ChevronRight className="h-3 w-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {totals.decided === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="text-booking-overview-empty">No booking decisions in the last 90 days.</p>
        ) : (
          <div className="flex flex-wrap items-center gap-6">
            <BookingStatusPie byStatus={byStatus} decided={totals.decided} />
            <div className="text-sm space-y-1">
              <div>
                <span className="text-muted-foreground">Approval rate: </span>
                <span className="font-semibold" data-testid="text-booking-overview-approval-rate">
                  {totals.approvalRate == null ? "—" : `${Math.round(totals.approvalRate * 100)}%`}
                </span>
              </div>
              {totals.medianDaysToDecision != null && (
                <div>
                  <span className="text-muted-foreground">Median time to decision: </span>
                  <span className="font-semibold">{totals.medianDaysToDecision}d</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
